/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  User, LogOut, Settings, BookOpen, ShieldCheck, Wallet, KeyRound, Pill, Activity, BarChart3, Star,
  MessageSquareText, Palette, Monitor, CreditCard, Mail, Copyright,
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/lib/auth';
import { initials, CATEGORY_META } from '@/lib/utils-cat';

/**
 * UserMenu — avatar dropdown in the top bar.
 * Data-driven: menu entries are declared in USER_MENU_SECTIONS and filtered by
 * `when(user)` predicates, which keeps the render function flat.
 * Extracted from AppShell.jsx (Code Quality refactor).
 */

const isPatient = (u) => u?.category === 'paziente';
const isProfessional = (u) => !!u && u.category !== 'paziente';
const isAdmin = (u) => !!u?.is_admin;
const always = () => true;

// label: i18n key (resolved with t) — text: literal label
const USER_MENU_SECTIONS = [
  [
    { key: 'profile', path: '/profile', Icon: User, label: 'profile', testId: 'menu-profile', when: always },
    { key: 'resources', path: '/resources', Icon: BookOpen, label: 'resources', testId: 'menu-resources', when: always },
    { key: 'security', path: '/security', Icon: KeyRound, label: 'menuSecurity', testId: 'menu-security', when: always },
    // Patient tools
    { key: 'wallet', path: '/wallet', Icon: Wallet, label: 'menuWallet', testId: 'menu-wallet', when: isPatient },
    { key: 'symptoms', path: '/ai/symptoms', Icon: Activity, text: 'Sintomi AI', testId: 'menu-symptom-checker', when: isPatient },
    { key: 'drugs-p', path: '/ai/drugs', Icon: Pill, text: 'Farmaci AI', testId: 'menu-drug-checker', when: isPatient },
    { key: 'second-opinion-p', path: '/second-opinion', Icon: MessageSquareText, text: 'Secondo Parere', testId: 'menu-second-opinion', when: isPatient },
    { key: 'my-reviews', path: '/my-reviews', Icon: Star, text: 'Le mie recensioni', testId: 'menu-my-reviews', when: isPatient },
    // Professional tools
    { key: 'insights', path: '/ai/insights', Icon: BarChart3, text: 'AI Insights', testId: 'menu-doctor-insights', when: (u) => !isPatient(u) },
    { key: 'drugs-d', path: '/ai/drugs', Icon: Pill, text: 'Drug Checker AI', testId: 'menu-drug-checker-doc', when: (u) => !isPatient(u) },
    { key: 'second-opinion-d', path: '/second-opinion', Icon: MessageSquareText, text: 'Secondi Pareri', testId: 'menu-second-opinion-doc', when: (u) => !isPatient(u) },
    { key: 'settings', path: '/settings', Icon: Settings, label: 'settings', testId: 'menu-settings', when: always },
    // Admin
    { key: 'admin', path: '/admin', Icon: ShieldCheck, label: 'admin', testId: 'menu-admin', when: isAdmin },
    { key: 'brand', path: '/admin/brand-settings', Icon: Palette, text: 'Personalizzazione brand', testId: 'menu-brand-settings', when: isAdmin },
    { key: 'stripe', path: '/admin/stripe-config', Icon: CreditCard, text: 'Configurazione Stripe', testId: 'menu-stripe-config', when: isAdmin },
    { key: 'email', path: '/admin/email', Icon: Mail, text: 'Email della clinica (SMTP)', testId: 'menu-email-settings', when: isAdmin },
    { key: 'license', path: '/legal/license', Icon: Copyright, text: 'Licenza & registro EULA', testId: 'menu-license-ip', when: isAdmin },
    { key: 'kiosk', path: '/kiosk', Icon: Monitor, text: "Modalità sala d'attesa", testId: 'menu-kiosk', when: isProfessional },
  ],
];

export const UserMenu = () => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const cat = user ? CATEGORY_META[user.category] : null;
  const CategoryIcon = cat?.icon;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-2 p-1 rounded-full hover:bg-secondary transition-colors ml-1" data-testid="topbar-user-menu">
          <Avatar className="h-8 w-8">
            {user?.avatar_base64 && <AvatarImage src={user.avatar_base64} />}
            <AvatarFallback className="text-xs font-semibold" style={{ backgroundColor: cat?.soft, color: cat?.textOnSoft }}>
              {initials(user?.full_name)}
            </AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <div className="px-2 py-2">
          <div className="text-sm font-semibold truncate flex items-center gap-1">
            {user?.full_name}
            {user?.is_admin && <ShieldCheck className="h-3.5 w-3.5 text-primary" />}
          </div>
          <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
          {cat && (
            <div className="mt-2 inline-flex items-center gap-1 text-[11px] rounded-full px-2 py-0.5" style={{ backgroundColor: cat.soft, color: cat.textOnSoft }}>
              {CategoryIcon && <CategoryIcon className="h-3 w-3" />}
              <span>{t(cat.label)}</span>
            </div>
          )}
        </div>
        {USER_MENU_SECTIONS.map((section, si) => (
          <React.Fragment key={`section-${si}`}>
            <DropdownMenuSeparator />
            {section.filter((item) => item.when(user)).map(({ key, path, Icon, label, text, testId }) => (
              <DropdownMenuItem key={key} onClick={() => navigate(path)} data-testid={testId}>
                <Icon className="h-4 w-4 mr-2" /> {label ? t(label) : text}
              </DropdownMenuItem>
            ))}
          </React.Fragment>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => { logout(); navigate('/login'); }} data-testid="menu-logout">
          <LogOut className="h-4 w-4 mr-2" /> {t('logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
